import React, { useEffect, useState, useCallback } from 'react';

/**
 * CropBoxOverlay renders the crop box and resize handles over the video.
 * Props:
 * - cropBox: {x, y, width, height}
 * - onCropBoxChange: (newBox) => void
 * - videoRef: ref to the video element
 */
const MIN_CROP_SIZE = 20;

const CropBoxOverlay = ({ cropBox, onCropBoxChange, videoRef }) => {
  const [isCropping, setIsCropping] = useState(false);
  const [resizeDir, setResizeDir] = useState(null);
  const [dragStart, setDragStart] = useState(null);

  // Helper to get video element's bounding rect
  const getVideoRect = useCallback(() => {
    if (!videoRef.current) return null;
    return videoRef.current.getBoundingClientRect();
  }, [videoRef]);

  // Mouse/touch handlers for crop box
  const handleCropMouseDown = (e) => {
    e.preventDefault();
    setIsCropping(true);
    setResizeDir(null);
    const rect = getVideoRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({
      x: clientX - rect.left - cropBox.x,
      y: clientY - rect.top - cropBox.y,
    });
  };

  const handleResizeMouseDown = (dir, e) => {
    e.stopPropagation();
    e.preventDefault();
    setResizeDir(dir);
    setIsCropping(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({
      x: clientX,
      y: clientY,
      cropBox: { ...cropBox },
    });
  };

  const handleCropMouseMove = useCallback(
    (e) => {
      if (!isCropping) return;
      const rect = getVideoRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      if (resizeDir) {
        // Resizing
        const dx = clientX - dragStart.x;
        const dy = clientY - dragStart.y;
        let { x, y, width, height } = dragStart.cropBox;
        switch (resizeDir) {
          case 'nw':
            x = Math.max(0, x + dx);
            y = Math.max(0, y + dy);
            width = Math.max(MIN_CROP_SIZE, width - dx);
            height = Math.max(MIN_CROP_SIZE, height - dy);
            break;
          case 'ne':
            y = Math.max(0, y + dy);
            width = Math.max(MIN_CROP_SIZE, width + dx);
            height = Math.max(MIN_CROP_SIZE, height - dy);
            break;
          case 'sw':
            x = Math.max(0, x + dx);
            width = Math.max(MIN_CROP_SIZE, width - dx);
            height = Math.max(MIN_CROP_SIZE, height + dy);
            break;
          case 'se':
            width = Math.max(MIN_CROP_SIZE, width + dx);
            height = Math.max(MIN_CROP_SIZE, height + dy);
            break;
          case 'n':
            y = Math.max(0, y + dy);
            height = Math.max(MIN_CROP_SIZE, height - dy);
            break;
          case 's':
            height = Math.max(MIN_CROP_SIZE, height + dy);
            break;
          case 'w':
            x = Math.max(0, x + dx);
            width = Math.max(MIN_CROP_SIZE, width - dx);
            break;
          case 'e':
            width = Math.max(MIN_CROP_SIZE, width + dx);
            break;
          default:
            break;
        }
        // Clamp to video bounds
        x = Math.max(0, Math.min(x, rect.width - MIN_CROP_SIZE));
        y = Math.max(0, Math.min(y, rect.height - MIN_CROP_SIZE));
        width = Math.max(MIN_CROP_SIZE, Math.min(width, rect.width - x));
        height = Math.max(MIN_CROP_SIZE, Math.min(height, rect.height - y));
        onCropBoxChange({ x, y, width, height });
      } else {
        // Moving
        let newX = clientX - rect.left - dragStart.x;
        let newY = clientY - rect.top - dragStart.y;
        newX = Math.max(0, Math.min(newX, rect.width - cropBox.width));
        newY = Math.max(0, Math.min(newY, rect.height - cropBox.height));
        onCropBoxChange({ ...cropBox, x: newX, y: newY });
      }
    },
    [isCropping, resizeDir, dragStart, cropBox, onCropBoxChange, getVideoRect]
  );

  const handleCropMouseUp = useCallback(() => {
    setIsCropping(false);
    setDragStart(null);
    setResizeDir(null);
  }, []);

  // Add mouse/touch event listeners to document for crop drag
  useEffect(() => {
    if (!isCropping) return;
    const move = (e) => handleCropMouseMove(e);
    const up = () => handleCropMouseUp();
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    document.addEventListener('touchmove', move);
    document.addEventListener('touchend', up);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', up);
    };
  }, [isCropping, dragStart, cropBox, resizeDir, handleCropMouseMove, handleCropMouseUp]);

  return (
    <div
      style={{
        position: 'absolute',
        left: cropBox.x,
        top: cropBox.y,
        width: cropBox.width,
        height: cropBox.height,
        border: '1px solid #00d4ff',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
        background: 'rgba(0,212,255,0.05)',
        cursor: resizeDir ? 'nwse-resize' : isCropping ? 'grabbing' : 'grab',
        display: 'block',
        zIndex: 10,
        pointerEvents: 'auto',
        boxSizing: 'border-box',
      }}
      onMouseDown={handleCropMouseDown}
      onTouchStart={handleCropMouseDown}
    >
      {/* Resize handles */}
      {['nw','n','ne','e','se','s','sw','w'].map(dir => {
        const size = 12;
        const style = {
          position: 'absolute',
          width: size, height: size,
          background: '#fff', border: '1px solid #00d4ff', borderRadius: 2, boxShadow: '0 0 2px rgba(0,0,0,0.5)',
          zIndex: 11,
          cursor: `${dir}-resize`,
          // Positioning
          ...(dir === 'nw' && { left: -size/2, top: -size/2 }),
          ...(dir === 'n' && { left: '50%', top: -size/2, transform: 'translateX(-50%)' }),
          ...(dir === 'ne' && { right: -size/2, top: -size/2 }),
          ...(dir === 'e' && { right: -size/2, top: '50%', transform: 'translateY(-50%)' }),
          ...(dir === 'se' && { right: -size/2, bottom: -size/2 }),
          ...(dir === 's' && { left: '50%', bottom: -size/2, transform: 'translateX(-50%)' }),
          ...(dir === 'sw' && { left: -size/2, bottom: -size/2 }),
          ...(dir === 'w' && { left: -size/2, top: '50%', transform: 'translateY(-50%)' }),
        };
        return (
          <div
            key={dir}
            style={style}
            onMouseDown={e => handleResizeMouseDown(dir, e)}
            onTouchStart={e => handleResizeMouseDown(dir, e)}
          />
        );
      })}
    </div>
  );
};

export default CropBoxOverlay;
